


























































import Vue, { PropType } from 'vue'
import forms from '@/mixins/forms-mixin'
import extensionsMixin from '@/mixins/extension-options-mixin'
import { FormField } from '@/types/Form.interface'
import { Container, Draggable } from 'vue-smooth-dnd'
import FieldFormComponent from '@/components/FieldForm.vue'
export default Vue.extend({
    mixins: [forms, extensionsMixin],
    components: {
        Container,
        Draggable,
        'field-form': FieldFormComponent
    },
    props: {
        defaultFields: {
            type: Array as PropType<FormField[]>,
            required: true
        },
        formFields: {
            type: Array as PropType<FormField[]>,
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        //  Determines if we have loaded everything
        mounted: false,
        awaitingExtensions: false
    }),
    mounted() {
        // Sort the fields such that the order matches
        this.formFields.sort((a: FormField, b: FormField) => {
            return a.order - b.order
        })

        // Indicate what we are done loading
        this.mounted = true
    },
    computed: {
        selectableFields(): FormField[] {
            // Get the selected form fields names
            const selectedFields = this.formFields.map((x: FormField) => x.name)

            //  Our actual selectable fields are the fields that have NOT been selected
            const selectable = this.defaultFields.filter((x: FormField) => {
                //  We want to filter out all fields that are currently selected
                return !selectedFields.includes(x.name)
            })

            // Return the selectable fields
            return selectable
        }
    },
    methods: {
        addField(field: FormField): void {
            // Get a default input
            const newInput = this.getDefaultInput(field.name)

            //  Grab the length of our form fields
            const len = this.formFields.length

            //  Our index is always going to be 1 ahead of the max length
            const idx = len + 1

            //  Set our new input order based on the index
            newInput.order = idx

            // Append a new field at the end of the form
            this.formFields.push(newInput)

            // Call the updateForm method
            this.updateForm()
        },
        checkOrder(): void {
            //  Iterate through our form fields
            for (let i = 0; i < this.formFields.length; i++) {
                //  Grab our current form field
                let field = this.formFields[i]

                //  If the order DOES match the current index, just skip
                if (field.order === i) continue

                //  Else, update the order
                field.order = i
                return this.updateForm()
            }
        },
        deleteField(field: FormField[]): void {
            // Get index to delete from
            const index = this.formFields.findIndex((f) => f === field)

            // If index found, delete the field
            if (index !== -1) {
                // Delete a field
                this.formFields.splice(index, 1)
            }

            // this.checkOrder() will NOT update the form if last field was removed
            if (index >= this.formFields.length - 1) {
                this.updateForm()
            }

            //  Evaluate order of fields (this will update the form)
            this.checkOrder()
        },
        fieldDuplicate(input: FormField): void {
            // Create a copy
            const temp = window._.cloneDeep(input)

            // Increment order from duplicated input
            temp.order = input.order + 1

            // Insert input into formFields array at its order position
            this.formFields.splice(temp.order - 1, 0, temp)

            // Check and update the order of the following fields
            this.checkOrder()
        },
        getChildPayload(index: number): number {
            return index
        },
        onDrop(dropResult): void {
            // Original index of input
            const index = dropResult.payload

            // Positive change to index
            const incr = dropResult.addedIndex

            // Negative change to index
            const decr = dropResult.removedIndex

            // New index to move to
            const newIndex = index + incr - decr

            // Copy input to new index and delete from old index
            this.formFields.splice(newIndex, 0, this.formFields.splice(index, 1)[0])

            // Update the order of the fields
            this.checkOrder()
        },
        popContactFieldHelp(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Help',
                text: `These are the contact profile fields that update whenever a contact submits the form.`,
                icon: 'mdi-help-circle',
                color: 'primary'
            })
        },
        popExtensionForm(): void {
            //  Instantiate a new extension option
            const extension = this.$store.getters.newExtensionOption

            //  Create an object for our app form props
            const props = {
                model: extension,
                inputs: this.$store.getters.extensionOptionInputs(extension),
                loading: false
            }

            //  Pop an app-form
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                title: 'Create New Custom Field',
                icon: 'mdi-format-annotation-plus',
                component: 'app-form',
                props: props,
                closeable: true,
                callback: async (extension) => {
                    //  Tell our loading prop to change
                    props.loading = true

                    //  Create our extension
                    await this.extensionCreate(extension)

                    //  We are done
                    props.loading = false
                }
            })
        },
        popFieldForm(): void {
            //  If they are all out of fields, then pop a warning
            if (this.selectableFields.length === 0) {
                return this.$store.dispatch('dialogOpen', {
                    type: 'MESSAGE',
                    title: 'Error',
                    text: 'No more fields available.',
                    icon: 'mdi-alert-circle-outline',
                    color: 'primary'
                })
            }

            //  Pop a create field dialog
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                title: 'Select field',
                titleStyles: 'text-align:center;',
                color: 'primary',
                closeable: true,
                closeOnSubmit: true,
                component: 'form-field-selector',
                props: {
                    availableFields: this.selectableFields
                },
                callback: (field) => {
                    //  If there is no field that was created then do nothing
                    if (!field) return

                    //  Else, go ahead and add it to our fields
                    this.addField(field)
                }
            })
        },
        popFieldTypeHelp(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Help',
                text: `Field types determine what information can be submitted. The following field types are text, numbers, dropdowns, dates, and phones. Text can contain alphanumeric characters. Numbers may only contain numeric characters. Dates are in ISO8601 format but are handled automatically by the calander input field. Dropdowns may only contain answers that mach one of the provided options. Phone numbers must contain 10-11 numeric characters.`,
                icon: 'mdi-help-circle',
                color: 'primary'
            })
        },
        popLabelHelp(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Help',
                text: `Labels are the text that appear on the form fields that the contact will see when they open your form.`,
                icon: 'mdi-help-circle',
                color: 'primary'
            })
        },
        popRequiredHelp(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Help',
                text: `Required fields must be filled out to be submitted.`,
                icon: 'mdi-help-circle',
                color: 'primary'
            })
        },
        async updateForm(): Promise<void> {
            //  Wait for this
            this.$emit('update')
        }
    }
})
